// How many times the app may throw the browser at Keycloak before it stops and
// says so instead.
//
// A signed-out visitor gets redirected — always, every time, that is the whole
// point of §05 having one way in. But "always" and "a browser stuck bouncing
// app -> Keycloak -> app forever" are the same instruction, so something has to
// tell them apart, and the difference is RATE, not history. A person who signs
// out and comes back ten minutes later is not a loop. Four full page loads in
// half a minute is.
//
// So this is a circuit breaker over a sliding episode, not a "have we been here
// before" flag: an earlier attempt never permanently disqualifies a later one,
// and the breaker re-closes on its own once the episode ages out. (An earlier
// version of this file did use a sticky boolean, and the bug it produced is the
// reason for the comment you are reading: sign out, navigate back, and the app
// showed a card instead of signing you in, forever, until the tab was closed.)
//
// The policy is a pure function of (record, now) so it can be tested for what
// it is — a decision table — instead of through a browser.

/** Redirect attempts inside one episode. `firstAt` is when the episode opened. */
export interface AttemptRecord {
  readonly count: number;
  readonly firstAt: number;
}

export type LoopDecision =
  | { readonly allow: true; readonly next: AttemptRecord }
  | { readonly allow: false };

/**
 * How long an episode stays open. Comfortably longer than a full bounce
 * (SPA -> login -> Keycloak -> callback -> SPA is well under a second on a
 * healthy realm), so a genuine loop always lands its attempts inside one
 * episode, while a human coming back later always opens a fresh one.
 */
export const LOOP_WINDOW_MS = 30_000;

/** Attempts allowed per episode before the breaker opens. */
export const MAX_ATTEMPTS = 3;

export const SIGN_IN_ATTEMPTS_KEY = "crm.sign-in-attempts";

/**
 * Whether to redirect now, and what the episode looks like afterwards.
 *
 * Note the asymmetry: an expired episode RESTARTS at one rather than being
 * topped up, so the budget is per-episode and a slow trickle of legitimate
 * sign-ins can never exhaust it.
 */
export function decideSignInRedirect(record: AttemptRecord | null, now: number): LoopDecision {
  if (record === null || now - record.firstAt > LOOP_WINDOW_MS) {
    return { allow: true, next: { count: 1, firstAt: now } };
  }
  if (record.count < MAX_ATTEMPTS) {
    return { allow: true, next: { count: record.count + 1, firstAt: record.firstAt } };
  }
  return { allow: false };
}

// sessionStorage rather than localStorage: the episode belongs to this tab and
// this browsing session, and it has to survive the trip to Keycloak and back —
// which it does, because the origin is unchanged on return. Every access fails
// soft; storage can throw outright in some privacy modes, and a breaker that
// cannot read its own state should let the user through, not wedge the app.

export function readAttempts(): AttemptRecord | null {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(SIGN_IN_ATTEMPTS_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { count, firstAt } = parsed as Partial<AttemptRecord>;
    // Anything hand-edited, truncated or written by an older version reads as
    // "no episode" — a fresh start is the safe reading, not a blocked one.
    if (typeof count !== "number" || !Number.isFinite(count)) return null;
    if (typeof firstAt !== "number" || !Number.isFinite(firstAt)) return null;
    return { count, firstAt };
  } catch {
    return null;
  }
}

export function writeAttempts(record: AttemptRecord): void {
  try {
    window.sessionStorage.setItem(SIGN_IN_ATTEMPTS_KEY, JSON.stringify(record));
  } catch {
    // Ignored: see above. Without storage the breaker cannot count, so a loop
    // would be bounded by nothing here — but a browser that cannot write a
    // session key also cannot keep a session cookie, which is its own problem.
  }
}

export function clearAttempts(): void {
  try {
    window.sessionStorage.removeItem(SIGN_IN_ATTEMPTS_KEY);
  } catch {
    // Ignored: see above.
  }
}
